import { useState, useCallback, useMemo } from 'react';
import type { LayoutChangeEvent, NativeScrollEvent } from 'react-native';
import { View, StyleSheet, ScrollView } from 'react-native';
import { Flex, ColorValues } from '@banhala/ui-kit';
import type { IconListProps, IconItem } from './types';
import type { AnyObject } from '../../../../types';
import { ShortcutIcon } from './components/ShortcutIcon';
import { isNil } from '../../../../utils';

interface LinePagerIndicatorProps {
  scrollPosition: number;
  contentWidth: number;
  containerWidth: number;
  itemCount: number;
}

/**
 * Android LinePagerIndicator와 동일한 동작 구현
 * - 트랙이 화면 중앙에 배치
 * - indicatorSize = 32dp
 * - 트랙 너비 = indicatorSize * totalItems / visibleItems
 * - 인디케이터는 스크롤 진행률에 따라 0 ~ (trackWidth - indicatorSize) 범위에서 이동
 */
const LinePagerIndicator = ({
  scrollPosition,
  contentWidth,
  containerWidth,
  itemCount,
}: LinePagerIndicatorProps) => {
  const indicatorSize = 32;

  const itemWidth = contentWidth / itemCount;
  const visibleItems = containerWidth / itemWidth;

  const trackWidth = indicatorSize * itemCount / visibleItems;

  const scrollableWidth = contentWidth - containerWidth;
  const progress =
    scrollableWidth > 0 ? Math.min(scrollPosition / scrollableWidth, 1) : 0;

  const indicatorPosition = progress * (trackWidth - indicatorSize);

  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      padding={{ vertical: 11 }}
    >
      <View
        style={[
          indicatorStyles.track,
          { width: trackWidth, backgroundColor: ColorValues.gray30 },
        ]}
      >
        <View
          style={[
            indicatorStyles.indicator,
            {
              width: indicatorSize,
              backgroundColor: ColorValues.gray70,
              marginLeft: indicatorPosition,
            },
          ]}
        />
      </View>
    </Flex>
  );
};

const indicatorStyles = StyleSheet.create({
  track: {
    height: 2,
    borderRadius: 1,
  },
  indicator: {
    height: 2,
    borderRadius: 1,
  },
});

type IconData = { item: IconItem; logging: AnyObject };

export const ScrollableIconList = ({ entity, body }: IconListProps) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const handleScroll = useCallback(
    (event: { nativeEvent: NativeScrollEvent }) => {
      setScrollPosition(event.nativeEvent.contentOffset.x);
    },
    [],
  );

  const handleContentSizeChange = useCallback((width: number) => {
    setContentWidth(width);
  }, []);

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  const description = body?.item ?? {
    row_size: 2,
    has_indicator: false,
    icon_size: 'MEDIUM' as const,
  };

  const rowSize = description.row_size || 2;
  const iconSize = description.icon_size === 'SMALL' ? 'small' : 'medium';
  const verticalGap = iconSize === 'small' ? 8 : 4;
  const hasIndicator = description.has_indicator;
  const canScroll = contentWidth > containerWidth;
  const showIndicator = hasIndicator && canScroll;

  const rowsData = useMemo(() => {
    if (isNil(entity)) return [];
    const rows: IconData[][] = [];
    for (let row = 0; row < rowSize; row++) {
      const rowData: IconData[] = [];
      for (let i = row; i < entity.length; i += rowSize) {
        rowData.push(entity[i]);
      }
      rows.push(rowData);
    }
    return rows;
  }, [entity, rowSize]);

  const columnCount = rowsData[0]?.length ?? 0;

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <Flex onLayout={handleLayout}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        onScroll={handleScroll}
        onContentSizeChange={handleContentSizeChange}
        scrollEventThrottle={16}
        contentContainerStyle={styles.scrollContent}
      >
        <Flex gap={verticalGap}>
          {rowsData.map((row, rowIndex) => (
            <Flex.Row key={rowIndex} alignItems="flex-start" gap={4}>
              {row.map((iconData, colIndex) => (
                <ShortcutIcon
                  key={colIndex}
                  item={iconData.item}
                  size={iconSize}
                  logging={iconData.logging}
                  analyticsEventName="CLICK_SCROLLABLE_ICON_LIST"
                />
              ))}
            </Flex.Row>
          ))}
        </Flex>
      </ScrollView>
      {showIndicator ? (
        <LinePagerIndicator
          scrollPosition={scrollPosition}
          contentWidth={contentWidth}
          containerWidth={containerWidth}
          itemCount={columnCount}
        />
      ) : (
        <Flex height={12} />
      )}
    </Flex>
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: 8,
  },
});
